import {
  ArrowForward20Regular,
  ArrowReply20Regular,
  Delete20Regular,
  Dismiss20Regular,
  Edit20Regular,
  Glasses20Regular,
  MailUnread20Regular,
  MoreHorizontal20Regular,
} from '@fluentui/react-icons';
import { Attachment as GraphAttachment } from '@microsoft/microsoft-graph-types';
import cn from 'classnames';
import { get } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';

import AttachmentsList from '../Attachments/AttachmentsList';
import {
  AttachmentBackgroundColor,
  AttachmentItem,
} from '../Attachments/AttachmentsList.interface';
import { Button } from '../Button/Button';
import { ThemeMode, useProviderTheme } from '../hooks';
import { OutlookColorIcon } from '../Icons/OutlookColorIcon';
import { Loader } from '../Loader/Loader';
import { Popup } from '../Popup/Popup';
import { stripScriptTags } from '../Utils/html/stripScriptTags';
import { EmailItem, EmailItemDetails } from './Email.interface';
import './Email.scss';
import { getDateDetailsLabel, replaceInlineAttachments } from './Email.utils';
import { EmailContentIframeMemoized } from './EmailContentIframe';
import EmailRecipientList from './EmailRecipientList';

export interface EmailDetailsProps {
  email: EmailItemDetails;
  isDarkTheme: boolean;
  isSmartphoneView: boolean;
  attachmentDownload: (
    emailItem: EmailItem,
    attachmentItem: AttachmentItem,
  ) => void | Promise<void>;
  editDraftClick: (emailItem: EmailItem) => void;
  deleteClick: (emailItem: EmailItem) => void;
  replyClick: (emailItem: EmailItem) => void;
  replyAllClick: (emailItem: EmailItem) => void;
  forwardClick: (emailItem: EmailItem) => void;
  markUnreadClick: (emailItem: EmailItem) => void;
  markReadClick: (emailItem: EmailItem) => void;
  // True, if the application is creating a new message draft
  // with all attachments.
  isCreatingForwardDraft: boolean;
  // True when a new draft is created as a response to an
  // original message
  isCreatingReplyDraft: boolean;
  showCloseEmailBtn?: boolean;
  closeEmailClick?: () => void;
}

/**
 * Replaces inline attachment images, removed script tags
 */
const prepareEmailBody = (emailItemDetails: EmailItemDetails) => {
  let content = replaceInlineAttachments(
    String(emailItemDetails.record.body?.content),
    emailItemDetails.inlineAttachments,
  );

  content = stripScriptTags(content);

  return content;
};

/**
 * Filters attachments for non-inline attachments, used for displaying
 * a list of downloadable attachments.
 */
const getNonInlineAttachments = (email: EmailItem): GraphAttachment[] => {
  const all = get(email, 'attachments', []);
  return all ? all.filter((a) => !a.isInline) : [];
};

/**
 * Renders email contents and displays basic meta information like recipients, subject, etc.
 */
const EmailDetails: React.FC<EmailDetailsProps> = ({
  email,
  isDarkTheme,
  isSmartphoneView,
  attachmentDownload,
  editDraftClick,
  deleteClick,
  replyClick,
  replyAllClick,
  forwardClick,
  markUnreadClick,
  markReadClick,
  isCreatingForwardDraft,
  isCreatingReplyDraft,
  showCloseEmailBtn,
  closeEmailClick,
}) => {
  const { t } = useTranslation();
  const { mode } = useProviderTheme();

  const senderAddress = get(email.record, 'from.emailAddress.address', false);
  const senderName = get(email.record, 'from.emailAddress.name', '');
  const content = prepareEmailBody(email);

  const ccRecipients = get(email, 'record.ccRecipients', []);
  const toRecipients = get(email, 'record.toRecipients', []);
  const nonInlineAttachments = getNonInlineAttachments(email.record);

  const handleOpenInOutlookClick = () => {
    if (email.record !== null && email.record.webLink !== null) {
      window.open(email.record.webLink, '_blank');
    }
  };

  const handleMarkUnreadClick = () => {
    if (email.record !== null) {
      markUnreadClick(email.record);
    }
  };

  const handleMarkReadClick = () => {
    if (email.record !== null) {
      markReadClick(email.record);
    }
  };

  const handleReplyAllClick = () => {
    if (email.record !== null) {
      replyAllClick(email.record);
    }
  };

  const getMenuItems = () => {
    const items: JSX.Element[] = [
      <Button
        key="btnOpenInOutlook"
        appearance="transparent"
        icon={<OutlookColorIcon />}
        aria-label={t('email_open_in_outlook_label', {
          defaultValue: 'Weiterer Tab mit Outlook.com wird geöffnet.',
        })}
        onClick={handleOpenInOutlookClick}
      >
        {t('email_open_in_outlook', {
          defaultValue: 'In Outlook öffnen',
        })}
      </Button>,
    ];

    if (isSmartphoneView) {
      items.push(
        <Button
          key="btnForward"
          appearance="transparent"
          icon={
            isCreatingForwardDraft ? (
              <Loader size="xs" />
            ) : (
              <ArrowForward20Regular className="iconForward" />
            )
          }
          disabled={isCreatingForwardDraft}
          onClick={() => forwardClick(email.record)}
        >
          {t('email_forward', { defaultValue: 'Weiterleiten' })}
        </Button>,
      );
    }

    if (email.record && !email.record.isDraft) {
      if (
        get(email, 'record.toRecipients', []).length > 1 ||
        get(email, 'record.ccRecipients', []).length > 1
      ) {
        items.push(
          <Button
            key="btnReplyAll"
            appearance="transparent"
            aria-label={t('email_reply_all_label', {
              defaultValue:
                'Öffnet den E-Mail Editor und bereitet die E-Mail vor und fügt alle Empfänger hinzu.',
            })}
            onClick={handleReplyAllClick}
            icon={
              isCreatingReplyDraft ? (
                <Loader size="xs" />
              ) : (
                <ArrowReply20Regular className="iconReply" />
              )
            }
          />,
        );
      }

      if (email.record.isRead) {
        items.push(
          <Button
            key="btnMarkUnread"
            appearance="transparent"
            aria-label={t('email_mark_unread_label', {
              defaultValue: 'E-Mail wird als ungelesen markiert.',
            })}
            icon={<MailUnread20Regular />}
            onClick={handleMarkUnreadClick}
          >
            {t('email_mark_unread', {
              defaultValue: 'Als ungelesen markieren',
            })}
          </Button>,
        );
      }

      if (!email.record.isRead) {
        items.push(
          <Button
            key="btnMarkRead"
            appearance="transparent"
            icon={<Glasses20Regular />}
            onClick={handleMarkReadClick}
          >
            {t('email_mark_read', {
              defaultValue: 'Als gelesen markieren',
            })}
          </Button>,
        );
      }
    }

    return items;
  };

  return (
    <div data-cy="emailDetails" className="emailDetailsWrapper">
      <div
        className={cn('emailDetailsHeader', {
          emailDetailsHeaderDark: mode === ThemeMode.Dark,
        })}
      >
        <div
          className={cn(
            'emailDetailActions',
            'flex items-center justify-end gap-2',
          )}
        >
          {showCloseEmailBtn && (
            <>
              <Button
                icon={<Dismiss20Regular />}
                appearance="transparent"
                onClick={closeEmailClick}
                aria-label={t('email_close_details', {
                  defaultValue: 'E-Mail-Vorschau schließen',
                })}
              />
              <div className="emailDetailActionsSpacer"></div>
            </>
          )}
          {!email.record.isDraft && (
            <>
              <Button
                icon={
                  isCreatingReplyDraft ? (
                    <Loader size="xs" />
                  ) : (
                    <ArrowReply20Regular className="iconReply" />
                  )
                }
                aria-label={t('email_reply_label', {
                  defaultValue:
                    'Antworten: Öffnet den E-Mail Editor und bereitet die E-Mail als Antwort vor.',
                })}
                disabled={isCreatingReplyDraft}
                onClick={() => replyClick(email.record)}
                data-testid="btnEmailReply"
              >
                {t('email_reply', { defaultValue: 'Antworten' })}
              </Button>
              {!isSmartphoneView && (
                <Button
                  icon={
                    isCreatingForwardDraft ? (
                      <Loader size="xs" />
                    ) : (
                      <ArrowForward20Regular className="iconForward" />
                    )
                  }
                  disabled={isCreatingForwardDraft}
                  onClick={() => forwardClick(email.record)}
                  data-testid="btnEmailforward"
                >
                  {t('email_forward', { defaultValue: 'Weiterleiten' })}
                </Button>
              )}
            </>
          )}
          {email.record.isDraft && (
            <Button
              icon={<Edit20Regular className="iconEdit" />}
              aria-label={t('edit_label', {
                defaultValue: 'E-Mail wird geöffnet zum Bearbeiten.',
              })}
              onClick={() => editDraftClick(email.record)}
              data-testid="btnEmailEdit"
            >
              {t('email_edit', { defaultValue: 'Bearbeiten' })}
            </Button>
          )}

          <Button
            icon={<Delete20Regular className="iconDelete" />}
            aria-label={t('delete_label', {
              defaultValue:
                'Löschen: Löscht die E-Mail und schließt das Mail-Editor Fenster',
            })}
            data-cy="deleteEmailButton"
            onClick={() => deleteClick(email.record)}
          >
            {t('delete', { defaultValue: 'Löschen' })}
          </Button>
          <Popup
            trigger={
              <Button
                icon={<MoreHorizontal20Regular />}
                aria-label={t('more_options', {
                  defaultValue: 'Mehr Optionen',
                })}
              />
            }
            position="below"
            content={
              <div className="flex flex-col items-start popup">
                {getMenuItems()}
              </div>
            }
          />
        </div>
        <ul className="emailDetailsHeaderList">
          <li className="headerDisplayEntry headerSubject">
            {get(email, 'record.subject', false) ??
              '(' +
                t('email_no_subject', { defaultValue: 'Kein Betreff' }) +
                ')'}
          </li>
          <li className="headerDisplayEntry headerRow">
            <span className="headerDisplayLabel">Datum:</span>
            {getDateDetailsLabel(email.record)}
          </li>
          {(senderAddress || senderName) && (
            <li className="headerDisplayEntry headerRow">
              <span className="headerDisplayLabel">Von:</span>{' '}
              {senderName && <span>{senderName}</span>}
              {senderAddress && <span>&lt;{senderAddress}&gt;</span>}
            </li>
          )}
          {toRecipients.length > 0 && (
            <li className="headerDisplayEntry headerRow">
              <span className="headerDisplayLabel">An:</span>
              <EmailRecipientList recipients={toRecipients} />
            </li>
          )}
          {ccRecipients.length > 0 && (
            <li className="headerDisplayEntry headerRow">
              <span className="headerDisplayLabel">CC:</span>
              <EmailRecipientList recipients={ccRecipients} />
            </li>
          )}
        </ul>
        <div className="attachmentsWrapper">
          <AttachmentsList
            attachments={nonInlineAttachments}
            className="attachementHeadItem"
            attachmentClick={(attachmentItem) => {
              attachmentDownload(email.record, attachmentItem);
            }}
            bgColor={AttachmentBackgroundColor.White}
          />
        </div>
      </div>

      <EmailContentIframeMemoized content={content} isDarkTheme={isDarkTheme} />
    </div>
  );
};

export default EmailDetails;
